// S5 · B: pure helpers of the « Partage » block (SharingCard) of a session, a horse or a team.
// No `@/` import, so `node --test` can load this file directly.
//
// GET {model}/{id}/sharing, for the object's owners (and an admin):
// { is_owner: true only for an owner from the object's pivot, never for an admin,
//   members: [{id, first_name, last_name, email, avatar, owner, job (horses only)}], owners first,
//   invitations: pending invitations, in the sent form (email, no `from`),
//   requests: pending access requests, in the received form (`from`, with horse_job on a session) }

import { hasValue } from './helpers/numeric.js'
import { KIND_INVITE, KIND_REQUEST, isPending, jobKey } from './invitations.js'

export const SHARING_MODELS = ['sessions', 'horses', 'teams']

export const sharingUrl = (model, id) =>
    SHARING_MODELS.includes(model) && hasValue(id) ? `/${model}/${id}/sharing` : null

const list = (value) => (Array.isArray(value) ? value : [])

// The three lists of the block, in their display order. Anything no longer pending
// (answered in the meantime from another screen) is left out.
export const sharingSections = (sharing) => ({
    requests: list(sharing?.requests).filter((row) => row?.kind === KIND_REQUEST && isPending(row)),
    members: list(sharing?.members),
    invitations: list(sharing?.invitations).filter((row) => row?.kind === KIND_INVITE && isPending(row)),
})

// Answering a request, cancelling or sending an invitation again: an owner from the
// pivot only (the API refuses an admin who is not linked to the object).
export const canManage = (sharing) => sharing?.is_owner === true

// The block that shows a row to whoever answers it ('accepted', 'refused', 'cancelled',
// 'resent'), wherever they answer: its owners answer the requests, and cancel or send
// again the invitations (so may their sender, who may no longer own it: a quiet 403).
// The person invited, or who asked, may not read it (403): null.
export const answeredSharingUrl = (invitation, answer) =>
    (invitation?.kind === KIND_REQUEST) === ['accepted', 'refused'].includes(answer)
        ? sharingUrl(invitation?.model, invitation?.model_id)
        : null

// Owners are never removed from this block, and nobody removes themselves here:
// the page keeps its own « leave » button for that.
export const canRemove = (member, userId) =>
    Boolean(member) && member.owner !== true && String(member.id) !== String(userId)

// The role of a horse member (rider, vet…), as a translation key, or null.
export const memberRoleKey = (member, model) => (model === 'horses' ? jobKey(member?.job) : null)

// Texts the block shares with the rest of the app, per kind of object.
const KEYS = {
    sessions: {
        members: 'sharing.members.sessions',
        invited: 'session.sharing.invited',
        removeConfirm: 'session.sharing.delete.confirm',
        removed: 'session.sharing.stop-sharing',
    },
    horses: {
        members: 'sharing.members.horses',
        invited: 'horse.sharing.invited',
        removeConfirm: 'horse.sharing.delete.confirm',
        removed: 'horse.sharing.stop-sharing',
    },
    teams: {
        members: 'sharing.members.teams',
        invited: 'team.team-user-invited',
        removeConfirm: 'team.sharing.delete.confirm',
        removed: 'team.sharing.stop-sharing',
    },
}

export const sharingKeys = (model) => KEYS[model] ?? KEYS.sessions
